import { memo, useEffect, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { LocationLiteModel } from 'models';

import { useEvent } from 'core/hooks';
import { findOrThrow, hasText } from 'core/utils';

import { useQueryLocationsLite } from 'features/api';
import { AuthenticationScheme, useAuthentication } from 'features/auth';
import { PatientVerbiageProvider } from 'features/patient/fragments/PatientVerbiageProvider';

import { LOCAL_STORAGE_KEY_SESSION_LOCATION_ID } from '../constants';
import { AppSessionLocationContext } from '../contexts';
import { AppSessionLocationContextValue, AppSessionLocationProviderProps } from '../types';

const EMPTY_ARRAY: LocationLiteModel[] = [];

export const AppSessionLocationProvider = memo<AppSessionLocationProviderProps>(({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { activeScheme } = useAuthentication();

  // Skip locations query for anonymous and share users, and when activeScheme is still initializing (undefined)
  // Share users get OPO information from patient data instead
  const shouldSkipLocationsQuery = activeScheme === undefined || activeScheme === AuthenticationScheme.ANONYMOUS || activeScheme === AuthenticationScheme.SHARE;

  // Conditionally call the hook - if we should skip, we'll get undefined data
  const locationsQuery = useQueryLocationsLite();
  const sessionLocationOptions = shouldSkipLocationsQuery ? undefined : locationsQuery.data;

  const sessionLocationIdParamRaw = searchParams.get('sessionLocationId');
  const sessionLocationIdParam = hasText(sessionLocationIdParamRaw) ? parseInt(sessionLocationIdParamRaw, 10) : null;

  // Effect to restore session location from localStorage if needed
  useEffect(() => {
    // Skip localStorage logic for share users since they don't need session location
    if (shouldSkipLocationsQuery) return;

    // If no URL parameter and we have location options, try to restore from localStorage
    if (!sessionLocationIdParam && sessionLocationOptions?.length) {
      const storedLocationId = localStorage.getItem(LOCAL_STORAGE_KEY_SESSION_LOCATION_ID);
      if (storedLocationId) {
        const locationId = parseInt(storedLocationId, 10);
        const storedLocation = sessionLocationOptions.find((loc) => loc.id === locationId);
        if (storedLocation) {
          // Update URL to reflect the restored location
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set('sessionLocationId', locationId.toString());
              return newParams;
            },
            { replace: true },
          );
        }
      }
    }
  }, [sessionLocationIdParam, sessionLocationOptions, setSearchParams, shouldSkipLocationsQuery]);

  const sessionLocation =
    shouldSkipLocationsQuery || sessionLocationOptions == null || !Number.isSafeInteger(sessionLocationIdParam)
      ? null
      : findOrThrow(sessionLocationOptions, (location) => location.id === sessionLocationIdParam);

  const setSessionLocation = useEvent((newLocation: LocationLiteModel | null, replaceHistory: boolean) => {
    // Skip localStorage operations for share users
    if (shouldSkipLocationsQuery) return;

    // Persist to localStorage
    if (newLocation) {
      localStorage.setItem(LOCAL_STORAGE_KEY_SESSION_LOCATION_ID, newLocation.id.toString());
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY_SESSION_LOCATION_ID);
    }

    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);

        if (newLocation == null && newParams.has('sessionLocationId')) {
          newParams.delete('sessionLocationId');
        } else if (newLocation != null) {
          newParams.set('sessionLocationId', newLocation.id.toString());
        }

        return newParams;
      },
      { replace: replaceHistory },
    );
  });

  const context: AppSessionLocationContextValue = useMemo(
    () => ({
      sessionLocationOptions: shouldSkipLocationsQuery ? EMPTY_ARRAY : (sessionLocationOptions ?? null),
      sessionLocation,
      setSessionLocation,
    }),
    [shouldSkipLocationsQuery, sessionLocationOptions, sessionLocation, setSessionLocation],
  );

  // For share users and during initialization, don't wrap with PatientVerbiageProvider
  // Share users: ShareInit handles that
  // During initialization: avoid trying to access sessionLocation when it's not available
  if (activeScheme === undefined || activeScheme === AuthenticationScheme.SHARE) {
    return <AppSessionLocationContext.Provider value={context}>{children}</AppSessionLocationContext.Provider>;
  }

  return (
    <AppSessionLocationContext.Provider value={context}>
      <PatientVerbiageProvider mode={sessionLocation?.isOpo ? 'Donor' : 'Patient'}>{children}</PatientVerbiageProvider>
    </AppSessionLocationContext.Provider>
  );
});

AppSessionLocationProvider.displayName = 'AppSessionLocationProvider';
